import {
  ComponentPropsWithoutRef,
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  createContext,
  Dispatch,
  SetStateAction,
  ReactNode,
  type MutableRefObject,
} from "react";
import cx from "classnames";

import {
  useCombobox as useDownshiftComboboxLegacy,
  useMultipleSelection as useDownshiftMultipleSelection,
  UseComboboxProps as UseDownshiftComboboxLegacyProps,
  UseMultipleSelectionProps as UseDownshiftMultipleSelectionProps,
  UseComboboxReturnValue as UseDownshiftComboboxLegacyReturnValue,
  UseMultipleSelectionReturnValue as UseDownshiftMultipleSelectionReturnValue,
  useSelect as useDownshiftSelect,
  UseSelectProps as UseDownshiftSelectProps,
  UseSelectReturnValue as UseDownshiftSelectReturnValue,
} from "downshift";
import { matchSorter, MatchSorterOptions } from "match-sorter";

import { PopoverLegacy as Popover } from "../PopoverLegacy";

import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

// import {
//   ComboboxLegacySearchField,
//   ComboboxLegacySelectTrigger,
// } from "./ComboboxLegacyTrigger";
// import { ComboboxLegacyContent } from "./ComboboxLegacyContent";
// import { ComboboxLegacyList } from "./ComboboxLegacyList";
// import {
//   ComboboxLegacyItem,
//   ComboboxLegacyItemAddNew,
// } from "./ComboboxLegacyItem";
// import { ComboboxLegacyEmpty } from "./ComboboxLegacyEmpty";
import {
  defaultItemToString,
  defaultItemToKey,
  useDownshiftEnvironment,
} from "./ComboboxLegacyUtils";

import styles from "./ComboboxLegacy.module.scss";

// const debugStateReducers = false;

export const ADD_NEW = Symbol.for("add-new");

export type ComboboxLegacyContextProps<Item = any> = Required<
  Pick<
    UseDownshiftComboboxLegacyProps<Item> | UseDownshiftSelectProps<Item>,
    "items" | "itemToString" | "itemToKey" | "selectedItem"
  >
> &
  Pick<
    | UseDownshiftComboboxLegacyReturnValue<Item>
    | UseDownshiftSelectReturnValue<Item>,
    | "getToggleButtonProps"
    | "getItemProps"
    | "getLabelProps"
    | "getMenuProps"
    | "closeMenu"
    | "selectItem"
    | "highlightedIndex"
    | "isOpen"
    | "inputValue"
  > &
  // Select-only doesn't have "getInputProps" so this needs to be made optional
  Partial<Pick<UseDownshiftComboboxLegacyReturnValue<Item>, "getInputProps">> &
  Partial<
    Pick<
      UseDownshiftMultipleSelectionReturnValue<Item>,
      | "getSelectedItemProps"
      | "getDropdownProps"
      | "addSelectedItem"
      | "removeSelectedItem"
      | "setSelectedItems"
      | "reset"
    >
  > &
  // Multiple and select-only both have "reset" but single-select doesn't
  Partial<
    Pick<
      | UseDownshiftComboboxLegacyReturnValue<Item>
      | UseDownshiftSelectReturnValue<Item>,
      "reset"
    >
  > &
  LayoutUtilProps & {
    disableClearSelection?: boolean;
    isReadOnly?: boolean;
    isDisabled?: boolean;
    /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
    setIsReadOnly: Dispatch<SetStateAction<undefined | boolean>>;
    /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
    setIsDisabled: Dispatch<SetStateAction<undefined | boolean>>;
    hasDisabledPopover: boolean;
    setHasDisabledPopover: Dispatch<SetStateAction<boolean>>;
    hasEmpty: boolean;
    setHasEmpty: Dispatch<SetStateAction<boolean>>;

    multiple: boolean;
    select: boolean;
    selectedItems: Item[];
    disabledItems: Item[];
    setDisabledItems: Dispatch<SetStateAction<Item[]>>;

    loading: boolean;

    referenceRef: MutableRefObject<HTMLDivElement | null>;
    contentRef: MutableRefObject<HTMLDivElement | null>;

    // select-only can't add new so these are optional
    hasAddNew?: boolean;
    setHasAddNew?: Dispatch<SetStateAction<boolean>>;
    onSelectItemAddNew?: (
      inputValue: UseDownshiftComboboxLegacyReturnValue<Item>["inputValue"],
    ) => void;
    setOnSelectItemAddNew?: Dispatch<
      SetStateAction<
        | ((
            inputValue: UseDownshiftComboboxLegacyReturnValue<Item>["inputValue"],
          ) => void)
        | undefined
      >
    >;
    hasExactMatch?: boolean;
    setHasExactMatch?: Dispatch<SetStateAction<boolean>>;
  };

export const ComboboxLegacyContext =
  createContext<ComboboxLegacyContextProps | null>(null);

export const useComboboxLegacy = function <Item = any>() {
  const context = useContext<ComboboxLegacyContextProps<Item> | null>(
    ComboboxLegacyContext,
  );
  if (context == null) {
    throw new Error("useComboboxLegacy must be wrapped in <ComboboxLegacy />");
  }
  return context;
};

type SharedProps = LayoutUtilProps &
  Pick<ComponentPropsWithoutRef<"div">, "className" | "style"> & {
    children?: ReactNode;
    /**
     * @default false
     */
    disabled?: boolean;
    /**
     * @default false
     */
    readOnly?: boolean;
    /**
     * @default false
     */
    loading?: boolean;
    /**
     * @default false
     */
    disableCloseOnClickOutside?: boolean;
    /**
     * @default false
     */
    disableMatchReferenceWidth?: boolean;

    root?: HTMLElement | React.MutableRefObject<HTMLElement | null> | null;

    /**
     * @default false
     */
    open?: boolean;
    /**
     * @default false
     */
    disableCloseOnSelectItem?: boolean;
  };

type SharedComboboxLegacyProps<Item> = Omit<
  UseDownshiftComboboxLegacyProps<Item>,
  "onStateChange" | "onInputValueChange"
> & {
  filterOptions?: MatchSorterOptions<Item>;

  /**
   * @deprecated Please use `ComboboxLegacy.SearchField`'s `onChange` method instead
   */
  onInputValueChange?: UseDownshiftComboboxLegacyProps<Item>["onInputValueChange"];
};

type SharedSelectProps<Item> = Omit<
  UseDownshiftSelectProps<Item>,
  "onStateChange" | "onInputValueChange"
>;

type SharedSingleProps<Item> = {
  multiple?: never;
  onChange?:
    | Dispatch<SetStateAction<Item | null>>
    | ((arg: Item | null) => void);
};

type SharedMultipleProps<Item> = {
  multiple: true;
  onChange?:
    | Dispatch<SetStateAction<Item[] | null>>
    | ((arg: Item[] | null) => void);
} & Partial<
  Pick<
    UseDownshiftMultipleSelectionProps<Item>,
    | "onSelectedItemsChange"
    | "initialSelectedItems"
    | "initialActiveIndex"
    | "defaultSelectedItems"
    | "defaultActiveIndex"
    | "activeIndex"
    | "selectedItems"
  >
>;

type SelectSingleProps<Item> = SharedProps &
  SharedSelectProps<Item> &
  SharedSingleProps<Item> & {
    disableClearSelection?: boolean;
  };

type SelectMultipleProps<Item> = SharedProps &
  SharedSelectProps<Item> &
  SharedMultipleProps<Item>;

type ComboboxLegacySingleProps<Item> = SharedProps &
  SharedComboboxLegacyProps<Item> &
  SharedSingleProps<Item>;

type ComboboxLegacyMultipleProps<Item> = Omit<
  SharedProps,
  "open" | "disableCloseOnSelectItem"
> &
  SharedComboboxLegacyProps<Item> &
  SharedMultipleProps<Item> &
  (
    | {
        // the `disableClearInputValueOnSelectItem` prop only makes sense if
        // the popover is being held open by `open={true}` or
        // `disableCloseOnSelectItem={true}`

        disableClearInputValueOnSelectItem: true;
        disableCloseOnSelectItem: true;
        open?: boolean;
      }
    | {
        disableClearInputValueOnSelectItem: true;
        open: true;
        disableCloseOnSelectItem?: boolean;
      }
    | {
        disableClearInputValueOnSelectItem?: never;
        disableCloseOnSelectItem?: boolean;
        open?: boolean;
      }
  );

export type ComboboxLegacyProps<Item = any> =
  | ComboboxLegacySingleProps<Item>
  | ComboboxLegacyMultipleProps<Item>;

type SelectProps<Item = any> =
  | SelectSingleProps<Item>
  | SelectMultipleProps<Item>;

export type ComboboxLegacySelectProps<Item> = SelectProps<Item>;

function isMultiple<Item>(
  props: ComboboxLegacyProps<Item> | SelectProps<Item>,
): props is ComboboxLegacyMultipleProps<Item> | SelectMultipleProps<Item> {
  return Object.hasOwn(props, "multiple") && props.multiple === true;
}

export const ComboboxLegacySingle = function <Item>(
  props: ComboboxLegacySingleProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    disabled,
    readOnly,
    items: itemsProp,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    filterOptions,
    loading = false,
    root,
    onInputValueChange,
    environment: environmentProp,
    ...rest
  } = componentProps;

  const ComboboxLegacyClassNames = cx(styles["combobox"], className);
  const [items, setItems] = useState(itemsProp);
  const [hasAddNew, setHasAddNew] = useState(false);
  const [onSelectItemAddNew, setOnSelectItemAddNew] = useState<
    | ((
        inputValue: UseDownshiftComboboxLegacyReturnValue<Item>["inputValue"],
      ) => void)
    | undefined
  >(undefined);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const handleOutsidePress = (e: MouseEvent) => {
    const clickedInsideReference =
      referenceRef.current &&
      e.target != null &&
      referenceRef.current.contains(e.target as Node);

    // only close if we didn't click the SearchField
    if (!clickedInsideReference) {
      closeMenu();
      return true;
    } else {
      return false;
    }
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const showAddNew = hasAddNew && !hasExactMatch;
  const combinedItems = useMemo(
    () => (showAddNew ? [...items, ADD_NEW as Item] : items),
    [items, showAddNew],
  );

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    setInputValue,
    inputValue,
    closeMenu,
  } = useDownshiftComboboxLegacy<Item>({
    ...rest,
    items: combinedItems,
    itemToString,
    itemToKey,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      // debugStateReducers && console.log(type, changes, { state });

      switch (type) {
        // keep ComboboxLegacy open onBlur, will call close using Popover's onOutsidePress
        case useDownshiftComboboxLegacy.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: open != null ? open : true,
            highlightedIndex: state.highlightedIndex,
          };

        case useDownshiftComboboxLegacy.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftComboboxLegacy.stateChangeTypes.ItemClick:
          if (changes.selectedItem === ADD_NEW) {
            // if we're adding a new item, don't change the selectedItem
            // and just call the event handler
            // TODO: Figure out a way to do this without having side-effects in
            //       the stateReducer
            const stateInputValue = state.inputValue;
            setTimeout(() => {
              onSelectItemAddNew?.(stateInputValue);
            }, 0); // setTimeout so we don't setState during render

            return {
              ...changes,
              selectedItem: state.selectedItem,
              inputValue: "",
            };
          } else {
            return {
              ...changes,
              isOpen:
                open != null
                  ? open
                  : disableCloseOnSelectItem
                    ? true
                    : changes.isOpen,
              highlightedIndex: disableCloseOnSelectItem
                ? 0
                : changes.highlightedIndex,
            };
          }

        // prevent this from closing the popover by default
        case useDownshiftComboboxLegacy.stateChangeTypes.InputClick:
          return { ...changes, isOpen: open != null ? open : true };

        default:
          return changes;
      }
    },
    onInputValueChange:
      onInputValueChange ??
      (({ inputValue, type, selectedItem }) => {
        const inputValueMatchesSelectedItem =
          selectedItem != null && inputValue === itemToString(selectedItem);
        const inputValueIsEmpty = inputValue == null || inputValue === "";

        if (
          type ===
            useDownshiftComboboxLegacy.stateChangeTypes.InputKeyDownEnter ||
          type === useDownshiftComboboxLegacy.stateChangeTypes.ItemClick
        ) {
          setItems(itemsProp);
          setHasExactMatch(true);
        } else if (!inputValueMatchesSelectedItem && !inputValueIsEmpty) {
          setItems(matchSorter(itemsProp, inputValue, filterOptions));
          setHasExactMatch(
            matchSorter(itemsProp, inputValue, {
              ...filterOptions,
              threshold: matchSorter.rankings.EQUAL,
            }).length > 0,
          );
        } else {
          setItems(itemsProp);
          setHasExactMatch(true);
        }
      }),
    onSelectedItemChange(changes) {
      const { selectedItem } = changes;

      if (onChange != null)
        onChange(selectedItem != null ? selectedItem : null);
    },
    onIsOpenChange({ isOpen, inputValue, selectedItem }) {
      if (isOpen === false) {
        // reset the filtering whenever we close the dropdown
        setItems(itemsProp);

        if (inputValue === "") {
          // special case: if the search field is empty, remove the selected item
          selectItem(null);
        } else {
          // otherwise, force the text to match the selected item
          setInputValue(selectedItem != null ? itemToString(selectedItem) : "");
        }
      }
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  // prevent prop sync useEffects from running on intiial render
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current === true) {
      firstRenderRef.current = false;
    }
  }, []);

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  // synchronize filtering state when "items" prop changes
  useEffect(() => {
    if (firstRenderRef.current) return;

    const inputValueMatchesSelectedItem =
      selectedItem != null && inputValue === itemToString(selectedItem);
    const inputValueIsEmpty = inputValue == null || inputValue === "";

    if (!inputValueMatchesSelectedItem && !inputValueIsEmpty) {
      setItems(matchSorter(itemsProp, inputValue, filterOptions));
      setHasExactMatch(
        matchSorter(itemsProp, inputValue, {
          ...filterOptions,
          threshold: matchSorter.rankings.EQUAL,
        }).length > 0,
      );
    } else {
      setItems(itemsProp);
      setHasExactMatch(true);
    }
  }, [itemsProp]);

  return (
    <ComboboxLegacyContext.Provider
      value={{
        multiple: false,
        select: false,
        items,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getInputProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems: [],
        disabledItems,
        setDisabledItems,
        getSelectedItemProps: undefined,
        getDropdownProps: undefined,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        hasAddNew,
        setHasAddNew,
        onSelectItemAddNew,
        setOnSelectItemAddNew,
        hasExactMatch,
        setHasExactMatch,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
      }}
    >
      <div
        className={ComboboxLegacyClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          root={root}
          open={isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onOutsidePress={handleOutsidePress}
        >
          {children}
        </Popover>
      </div>
    </ComboboxLegacyContext.Provider>
  );
};

export const ComboboxLegacyMultiple = function <Item>(
  props: ComboboxLegacyMultipleProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    items: itemsProp,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    onSelectedItemsChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    disableClearInputValueOnSelectItem = false,
    filterOptions,
    loading = false,
    root,
    disabled,
    readOnly,
    stateReducer: _,
    onInputValueChange,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp,
    ...rest
  } = componentProps;

  const ComboboxLegacyClassNames = cx(styles["combobox"], className);
  const [items, setItems] = useState(itemsProp);
  const [hasAddNew, setHasAddNew] = useState(false);
  const [onSelectItemAddNew, setOnSelectItemAddNew] = useState<
    | ((
        inputValue: UseDownshiftComboboxLegacyReturnValue<Item>["inputValue"],
      ) => void)
    | undefined
  >(undefined);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  // Downshift's useComboboxLegacy and useMultipleSelection have a kinda sorta
  // circular dependency, so storing setInputValue here to allow setting the
  // inputValue in the useMultipleSelection change handlers
  let storedSetInputValue: ((inputValue: string) => void) | undefined =
    undefined;

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const handleOutsidePress = (e: MouseEvent) => {
    const clickedInsideReference =
      referenceRef.current &&
      e.target != null &&
      referenceRef.current.contains(e.target as Node);

    // only close if we didn't click the SearchField
    if (!clickedInsideReference) {
      closeMenu();
      return true;
    } else {
      return false;
    }
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const showAddNew = hasAddNew && !hasExactMatch;
  const combinedItems = useMemo(
    () => (showAddNew ? [...items, ADD_NEW as Item] : items),
    [items, showAddNew],
  );

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const {
    getSelectedItemProps,
    getDropdownProps,
    reset,
    selectedItems,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
  } = useDownshiftMultipleSelection<Item>({
    itemToKey,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp ?? environment,
    stateReducer(state, { type, changes }) {
      // debugStateReducers && console.log(type, changes, { state });

      switch (type) {
        // the Chip itself will handle "Backspace" and "Delete"
        // just return previous state
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownBackspace:
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownDelete:
          return state;

        case useDownshiftMultipleSelection.stateChangeTypes
          .FunctionRemoveSelectedItem:
          return { ...changes, activeIndex: -1 };

        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          return { ...changes, selectedItems: [] };

        default:
          return changes;
      }
    },
    onStateChange({ type }) {
      switch (type) {
        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          storedSetInputValue?.("");
          break;
      }
    },
    onSelectedItemsChange(changes) {
      const { selectedItems } = changes;
      if (onChange != null) onChange(selectedItems);
      if (onSelectedItemsChange != null) onSelectedItemsChange(changes);
    },
  });

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    setInputValue,
    inputValue,
    closeMenu,
  } = useDownshiftComboboxLegacy<Item>({
    ...rest,
    items: combinedItems,
    itemToString,
    itemToKey,
    selectedItem: null,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      // debugStateReducers && console.log(type, changes, { state });

      switch (type) {
        // keep ComboboxLegacy open onBlur, will call close using Popover's onOutsidePress
        case useDownshiftComboboxLegacy.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: state.inputValue,
            selectedItem: null,
            isOpen: open != null ? open : true,
            highlightedIndex: state.highlightedIndex,
          };

        case useDownshiftComboboxLegacy.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftComboboxLegacy.stateChangeTypes.ItemClick:
          if (changes.selectedItem === ADD_NEW) {
            // if we're adding a new item, don't change the selectedItem
            // and just call the event handler
            // TODO: Figure out a way to do this without having side-effects in
            //       the stateReducer
            const stateInputValue = state.inputValue;
            setTimeout(() => {
              onSelectItemAddNew?.(stateInputValue);
            }, 0); // setTimeout so we don't setState during render

            return {
              ...changes,
              selectedItem: state.selectedItem,
              inputValue: "",
            };
          } else {
            return {
              ...changes,
              isOpen:
                open != null
                  ? open
                  : disableCloseOnSelectItem
                    ? true
                    : changes.isOpen,
              highlightedIndex: disableCloseOnSelectItem
                ? disableClearInputValueOnSelectItem || !state.inputValue
                  ? state.highlightedIndex
                  : 0
                : changes.highlightedIndex,
              inputValue: disableClearInputValueOnSelectItem
                ? state.inputValue
                : "",
            };
          }

        // prevent this from closing the popover by default
        case useDownshiftComboboxLegacy.stateChangeTypes.InputClick:
          return { ...changes, isOpen: open != null ? open : true };

        // prevent this from clearing the input value by default
        case useDownshiftComboboxLegacy.stateChangeTypes
          .ControlledPropUpdatedSelectedItem:
          return { ...changes, inputValue: state.inputValue };

        default:
          return changes;
      }
    },
    onStateChange({ type, selectedItem: newSelectedItem }) {
      switch (type) {
        case useDownshiftComboboxLegacy.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftComboboxLegacy.stateChangeTypes.ItemClick:
        case useDownshiftComboboxLegacy.stateChangeTypes.InputBlur:
          if (newSelectedItem != null) {
            const itemAlreadySelected = selectedItems.some(
              (item) => itemToKey(item) === itemToKey(newSelectedItem),
            );

            if (itemAlreadySelected) {
              removeSelectedItem(newSelectedItem);
            } else {
              addSelectedItem(newSelectedItem);
            }
          }
          break;

        default:
          break;
      }
    },
    onInputValueChange:
      onInputValueChange ??
      (({ inputValue, ...changes }) => {
        const { type, selectedItem } = changes;

        const inputValueMatchesSelectedItem =
          selectedItem != null && inputValue === itemToString(selectedItem);
        const inputValueIsEmpty = inputValue == null || inputValue === "";

        if (
          type ===
            useDownshiftComboboxLegacy.stateChangeTypes.InputKeyDownEnter ||
          type === useDownshiftComboboxLegacy.stateChangeTypes.ItemClick
        ) {
          setItems(itemsProp);
          setHasExactMatch(true);
        } else if (!inputValueMatchesSelectedItem && !inputValueIsEmpty) {
          setItems(matchSorter(itemsProp, inputValue, filterOptions));
          setHasExactMatch(
            matchSorter(itemsProp, inputValue, {
              ...filterOptions,
              threshold: matchSorter.rankings.EQUAL,
            }).length > 0,
          );
        } else {
          setItems(itemsProp);
          setHasExactMatch(true);
        }
      }),
    onIsOpenChange({ isOpen }) {
      if (isOpen === false) {
        setInputValue("");

        setItems(itemsProp);
      }
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  // Now that useDownshiftComboboxLegacy has returned the setInputValue function,
  // we can store it here to call later inside of useDownshiftMultipleSelection
  storedSetInputValue = setInputValue;

  // prevent prop sync useEffects from running on intiial render
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current === true) {
      firstRenderRef.current = false;
    }
  }, []);

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  // synchronize filtering state when "items" prop changes
  useEffect(() => {
    if (firstRenderRef.current) return;

    const inputValueIsEmpty = inputValue == null || inputValue === "";

    if (!inputValueIsEmpty) {
      setItems(matchSorter(itemsProp, inputValue, filterOptions));
      setHasExactMatch(
        matchSorter(itemsProp, inputValue, {
          ...filterOptions,
          threshold: matchSorter.rankings.EQUAL,
        }).length > 0,
      );
    } else {
      setItems(itemsProp);
      setHasExactMatch(true);
    }
  }, [itemsProp]);

  return (
    <ComboboxLegacyContext.Provider
      value={{
        multiple: true,
        select: false,
        items,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getInputProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems,
        disabledItems,
        setDisabledItems,
        getSelectedItemProps,
        getDropdownProps,
        reset,
        addSelectedItem,
        removeSelectedItem,
        setSelectedItems,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        hasAddNew,
        setHasAddNew,
        onSelectItemAddNew,
        setOnSelectItemAddNew,
        hasExactMatch,
        setHasExactMatch,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
      }}
    >
      <div
        className={ComboboxLegacyClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          root={root}
          open={!isDisabled && isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onOutsidePress={handleOutsidePress}
        >
          {children}
        </Popover>
      </div>
    </ComboboxLegacyContext.Provider>
  );
};

/**
 * NOTE: There is a lot of duplicated code here! Here's a list of the /actual/
 *       differences between select-only and regular combobox to help with
 *       refactoring this later!
 *
 *       1) Using Downshift's `useSelect` hook instead of `useComboboxLegacy`
 *          - Swapping out the stateChangeTypes for their equivalents
 *       2) Since there's no input, we don't need to passthrough `getInputProps`
 *       3) Since we don't do any sorting or filtering, we can remove:
 *          - onInputValueChange function and prop
 *            - setInputValue function
 *          - onIsOpenChange function
 *          - filterOptions prop
 *          - internal `items` state and useEffect to keep it in sync
 *            - useEffect to track first render
 *       4) Multiple-specific
 *          - Remove `disableClearInputValueOnSelectItem` prop
 *          - No need to store `setInputValue` function
 *          - Remove all state reducer references to `inputValue`
 *          - Remove `onStateChange` function from `useMultipleSelection`
 */

export const SelectLegacySingle = function <Item>(
  props: SelectSingleProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    disabled,
    readOnly,
    items,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    disableClearSelection = false,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    loading = false,
    root,
    environment: environmentProp,
    ...rest
  } = componentProps;

  const ComboboxLegacyClassNames = cx(styles["combobox"], className);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const handleOutsidePress = (e: MouseEvent) => {
    const clickedInsideReference =
      referenceRef.current &&
      e.target != null &&
      referenceRef.current.contains(e.target as Node);

    // only close if we didn't click the SearchField
    if (!clickedInsideReference) {
      closeMenu();
      return true;
    } else {
      return false;
    }
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const {
    isOpen,
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    inputValue,
    closeMenu,
  } = useDownshiftSelect<Item>({
    ...rest,
    items,
    itemToString,
    itemToKey,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      // debugStateReducers && console.log(type, changes, { state });

      switch (type) {
        // keep ComboboxLegacy open onBlur, will call close using Popover's onOutsidePress
        case useDownshiftSelect.stateChangeTypes.ToggleButtonBlur:
          return {
            ...changes,
            isOpen: open != null ? open : true,
            highlightedIndex: state.highlightedIndex,
          };

        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen:
              open != null
                ? open
                : disableCloseOnSelectItem
                  ? true
                  : changes.isOpen,
            highlightedIndex: disableCloseOnSelectItem
              ? 0
              : changes.highlightedIndex,
          };

        // prevent this from closing the popover by default
        case useDownshiftSelect.stateChangeTypes.ToggleButtonClick:
          return { ...changes, isOpen: open != null ? open : true };

        default:
          return changes;
      }
    },
    onSelectedItemChange(changes) {
      const { selectedItem } = changes;

      if (onChange != null)
        onChange(selectedItem != null ? selectedItem : null);
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  return (
    <ComboboxLegacyContext.Provider
      value={{
        multiple: false,
        select: true,
        disableClearSelection,
        items,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems: [],
        disabledItems,
        setDisabledItems,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
      }}
    >
      <div
        className={ComboboxLegacyClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          root={root}
          open={isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onOutsidePress={handleOutsidePress}
        >
          {children}
        </Popover>
      </div>
    </ComboboxLegacyContext.Provider>
  );
};

export const SelectLegacyMultiple = function <Item>(
  props: SelectMultipleProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    items,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    onSelectedItemsChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    loading = false,
    root,
    disabled,
    readOnly,
    stateReducer: _,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp,
    ...rest
  } = componentProps;

  const ComboboxLegacyClassNames = cx(styles["combobox"], className);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by ComboboxLegacy's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const handleOutsidePress = (e: MouseEvent) => {
    const clickedInsideReference =
      referenceRef.current &&
      e.target != null &&
      referenceRef.current.contains(e.target as Node);

    // only close if we didn't click the SearchField
    if (!clickedInsideReference) {
      closeMenu();
      return true;
    } else {
      return false;
    }
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const {
    getSelectedItemProps,
    getDropdownProps,
    reset,
    selectedItems,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
  } = useDownshiftMultipleSelection<Item>({
    itemToKey,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp ?? environment,
    stateReducer(state, { type, changes }) {
      // debugStateReducers && console.log(type, changes, { state });

      switch (type) {
        // the Chip itself will handle "Backspace" and "Delete"
        // just return previous state
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownBackspace:
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownDelete:
          return state;

        case useDownshiftMultipleSelection.stateChangeTypes
          .FunctionRemoveSelectedItem:
          return { ...changes, activeIndex: -1 };

        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          return { ...changes, selectedItems: [] };

        default:
          return changes;
      }
    },
    onSelectedItemsChange(changes) {
      const { selectedItems } = changes;
      if (onChange != null) onChange(selectedItems);
      if (onSelectedItemsChange != null) onSelectedItemsChange(changes);
    },
  });

  const {
    isOpen,
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    inputValue,
    closeMenu,
  } = useDownshiftSelect<Item>({
    ...rest,
    items,
    itemToString,
    itemToKey,
    selectedItem: null,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      // debugStateReducers && console.log(type, changes, { state });

      switch (type) {
        // keep ComboboxLegacy open onBlur, will call close using Popover's onOutsidePress
        case useDownshiftSelect.stateChangeTypes.ToggleButtonBlur:
          return {
            ...changes,
            selectedItem: null,
            isOpen: open != null ? open : true,
            highlightedIndex: state.highlightedIndex,
          };

        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen:
              open != null
                ? open
                : disableCloseOnSelectItem
                  ? true
                  : changes.isOpen,
            highlightedIndex: disableCloseOnSelectItem
              ? 0
              : changes.highlightedIndex,
          };

        // prevent this from closing the popover by default
        case useDownshiftSelect.stateChangeTypes.ToggleButtonClick:
          return { ...changes, isOpen: open != null ? open : true };

        default:
          return changes;
      }
    },
    onStateChange({ type, selectedItem: newSelectedItem }) {
      switch (type) {
        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
        case useDownshiftSelect.stateChangeTypes.ToggleButtonBlur:
          if (newSelectedItem != null) {
            const itemAlreadySelected = selectedItems.some(
              (item) => itemToKey(item) === itemToKey(newSelectedItem),
            );

            if (itemAlreadySelected) {
              removeSelectedItem(newSelectedItem);
            } else {
              addSelectedItem(newSelectedItem);
            }
          }
          break;

        default:
          break;
      }
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  return (
    <ComboboxLegacyContext.Provider
      value={{
        multiple: true,
        select: true,
        items,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems,
        disabledItems,
        setDisabledItems,
        getSelectedItemProps,
        getDropdownProps,
        reset,
        addSelectedItem,
        removeSelectedItem,
        setSelectedItems,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
      }}
    >
      <div
        className={ComboboxLegacyClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          root={root}
          open={!isDisabled && isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onOutsidePress={handleOutsidePress}
        >
          {children}
        </Popover>
      </div>
    </ComboboxLegacyContext.Provider>
  );
};

// multi-select and single-select need to be separate components because
// we can't call hooks conditionally
export const ComboboxLegacyElement = function <Item>(
  props: ComboboxLegacyProps<Item>,
) {
  if (isMultiple(props)) {
    return <ComboboxLegacyMultiple<Item> {...props} />;
  } else {
    return <ComboboxLegacySingle<Item> {...props} />;
  }
};

export const ComboboxLegacySelect = function <Item>(props: SelectProps<Item>) {
  if (isMultiple(props)) {
    return <SelectLegacyMultiple<Item> {...props} />;
  } else {
    return <SelectLegacySingle<Item> {...props} />;
  }
};

// export const ComboboxLegacy = Object.assign(ComboboxLegacyElement, {
//   Select: ComboboxLegacySelect,
//   SearchField: ComboboxLegacySearchField,
//   SelectTrigger: ComboboxLegacySelectTrigger,
//   Content: ComboboxLegacyContent,
//   List: ComboboxLegacyList,
//   Item: ComboboxLegacyItem,
//   ItemAddNew: ComboboxLegacyItemAddNew,
//   Empty: ComboboxLegacyEmpty,
// });
